import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FaChartLine, FaArrowRight, FaLeaf } from "react-icons/fa";

const statIconMapping = {
  FaChartLine: FaChartLine,
  FaArrowRight: FaArrowRight,
  FaLeaf: FaLeaf,
};

const CaseStudyCard = React.memo(
  ({ study, index, isActive, setActiveCase, caseImages }) => {
    const imagesArray = useMemo(
      () => [caseImages.project1, caseImages.project2],
      [caseImages]
    );

    const caseImage = imagesArray[index % imagesArray.length] || imagesArray[0];

    return (
      <motion.div
        key={index}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.2 }}
        className={`
        bg-gray-800 rounded-xl shadow-xl overflow-hidden group hover:shadow-2xl transition-all duration-500
        flex flex-col justify-between
        ${isActive ? "ring-2 ring-teal-400 transform scale-[1.01]" : ""}
      `}
        onMouseEnter={() => setActiveCase(index)}
        onMouseLeave={() => setActiveCase(null)}
      >
        <div>
          <div className="relative h-48 overflow-hidden">
            <img
              src={caseImage}
              alt={study.client}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/70 via-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4 text-teal-200">
              <p className="text-sm lg:text-md font-medium">{study.location}</p>
            </div>
          </div>

          <div className="p-6 pb-0">
            <div className="flex justify-between items-start mb-4">
              <div className="w-2/3 pr-3">
                <h3 className="text-sm md:text-lg font-bold text-white mb-2">
                  {study.client}
                </h3>
                <p className="text-sm lg:text-md text-emerald-400 font-semibold">
                  {study.result}
                </p>
                <p className="text-sm lg:text-md text-gray-400 mt-2">
                  {study.description}
                </p>
              </div>
              <span className="w-1/3 text-right text-sm lg:text-md text-teal-400 font-bold">
                {study.savings}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 pt-0">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {study.stats.map((stat, idx) => {
              const IconComponent = statIconMapping[stat.icon];
              return (
                <motion.div
                  key={idx}
                  whileHover={{ scale: 1.05 }}
                  className="bg-gray-700 p-3 rounded-lg border border-gray-600 hover:shadow-md transition-all duration-300 flex flex-col justify-between"
                >
                  {IconComponent && (
                    <IconComponent className={`w-5 h-5 ${stat.color} mb-1`} />
                  )}
                  <p className="text-md lg:text-lg font-bold text-white">
                    {stat.value}
                  </p>
                  <p className="text-xs text-gray-400">{stat.label}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    );
  }
);

CaseStudyCard.displayName = "CaseStudyCard";

CaseStudyCard.propTypes = {
  study: PropTypes.shape({
    client: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    result: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    savings: PropTypes.string.isRequired,
    stats: PropTypes.arrayOf(
      PropTypes.shape({
        icon: PropTypes.string.isRequired,
        color: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
      })
    ).isRequired,
  }).isRequired,
  index: PropTypes.number.isRequired,
  isActive: PropTypes.bool.isRequired,
  setActiveCase: PropTypes.func.isRequired,
  caseImages: PropTypes.shape({
    project1: PropTypes.string.isRequired,
    project2: PropTypes.string.isRequired,
  }).isRequired,
};

function CaseStudies({ caseImages }) {
  const { t } = useTranslation();
  const [activeCase, setActiveCase] = useState(null);

  const caseData = useMemo(() => {
    return t("components.solution.caseStudies", {
      returnObjects: true,
    });
  }, [t]);

  const { sectionTitle, sectionSubtitle, studies } = caseData;

  return (
    <section className="py-12 bg-black" id="safePad">
      <div className="max-w-6xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-4">
            {sectionTitle}
          </h2>
          <div className="w-32 h-1.5 bg-linear-to-r from-teal-400 to-green-400 mx-auto mb-6 rounded-full" />
          <p className="text-md lg:text-lg text-gray-400 max-w-3xl mx-auto">
            {sectionSubtitle}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {studies.map((study, index) => (
            <CaseStudyCard
              key={index}
              study={study}
              index={index}
              isActive={activeCase === index}
              setActiveCase={setActiveCase}
              caseImages={caseImages}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

CaseStudies.propTypes = {
  caseImages: PropTypes.shape({
    project1: PropTypes.string.isRequired,
    project2: PropTypes.string.isRequired,
  }).isRequired,
};

export default CaseStudies;
